import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Flex,
  Link,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
// import { cpf as cpfValid } from 'cpf-cnpj-validator'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import InputMask from 'react-input-mask'

import api from '../services/api'
import apiViaCep from '../services/apiViaCep'

import { Input } from './Form/Input'
import { Checkbox } from './Form/Checkbox'
import { TextArea } from './Form/TextArea'

import { LandingPageProcessDTO } from '../dtos/LandingPageProcessDTO'

const contactUsFormSchema = z.object({
  name: z
    .string()
    .min(10, { message: 'O campo nome completo é obrigatório.' })
    .toUpperCase(),
  birthDate: z.string(),
  cellPhone: z.string().min(14, { message: 'O campo celular é obrigatório.' }),
  email: z
    .string()
    .email({ message: 'O campo e-mail é obrigatório.' })
    .toLowerCase(),
  zipCode: z.string().optional(),
  address: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  district: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  question1: z.string().optional(),
  question2: z.string().optional(),
  question3: z.string().optional(),
  observation: z.string().optional(),
})

type ContactUsFormData = z.infer<typeof contactUsFormSchema>

interface FormContactUsProps {
  landingPageProcess: LandingPageProcessDTO
}

export function FormContactUs({ landingPageProcess }: FormContactUsProps) {
  const [checkedItems, setCheckedItems] = useState([true, true, true])
  const [toastOptions, setToastOptions] = useState(false)
  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<ContactUsFormData>({
    resolver: zodResolver(contactUsFormSchema),
  })

  const toast = useToast()
  const navigate = useNavigate()

  const allChecked = checkedItems.every(Boolean)
  const isIndeterminate = checkedItems.some(Boolean) && !allChecked

  async function handleContactUs(data: ContactUsFormData) {
    try {
      await api.post('contacts', {
        landingPageId: Number(landingPageProcess.id),
        name: data.name,
        email: data.email,
        cellPhone: data.cellPhone.replace(/\D/g, ''),
        birthDate: data.birthDate,
        zipCode: !data.zipCode ? '' : data.zipCode.replace(/\D/g, ''),
        address: !data.address ? '' : data.address,
        number: !data.number ? '' : data.number,
        complement: !data.complement ? '' : data.complement,
        district: !data.district ? '' : data.district,
        city: !data.city ? '' : data.city,
        state: !data.state ? '' : data.state,
        question1: !data.question1 ? '' : data.question1,
        question2: !data.question2 ? '' : data.question2,
        question3: !data.question3 ? '' : data.question3,
        observation: !data.observation ? '' : data.observation,
        acceptCommunicationEmail: checkedItems[0],
        acceptCommunicationSms: checkedItems[1],
        acceptCommunicationWhatsapp: checkedItems[2],
      })

      setValue('name', '')
      setValue('email', '')
      setValue('cellPhone', '')
      setValue('birthDate', '')

      navigate('agradecimento')
    } catch (err) {
      toast({
        title: 'Error ao efetuar o cadastro!',
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  }

  async function handleSearchZipCode(zipCode: string) {
    try {
      const addressResponse = await apiViaCep.get(`${zipCode}/json`)

      setValue('address', addressResponse.data.logradouro)
      setValue('district', addressResponse.data.bairro)
      setValue('city', addressResponse.data.estado)
      setValue('state', addressResponse.data.uf)
    } catch (err) {
      toast({
        title: 'Error ao efetuar o cadastro!',
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  }

  return (
    <Flex
      w="100%"
      justify="center"
      align="center"
      gap="8"
      p="4"
      id="contact"
    >
      <Flex
        as="form"
        onSubmit={handleSubmit(handleContactUs)}
        maxW={1100}
        w="100%"
        align="center"
        justify="center"
        flexDirection="column"
        py="8"
        px="14"
        borderRadius={20}
        bgColor="gray.100"
        shadow="xl"
      >
        <Text color={landingPageProcess.colorBase} textAlign="center" my="6">
          {landingPageProcess.titleForm}
        </Text>

        <Flex flexDirection="column" width="100%" gap="4">
          <Input
            label="(*) Nome Completo:"
            type="text"
            placeholder="Digite seu nome completo"
            {...register('name')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.name}
          />

          <Input
            label="(*) E-mail:"
            type="email"
            placeholder="Digite seu e-mail"
            {...register('email')}
            colorLabel={landingPageProcess.colorBase}
            error={errors.email}
          />

          <Flex flexDirection={['column', 'column', 'row']} gap="4">
            <Input
              as={InputMask}
              mask="(99)99999-9999"
              label="(*) Celular:"
              type="text"
              placeholder="Digite seu celular"
              {...register('cellPhone')}
              colorLabel={landingPageProcess.colorBase}
              error={errors.cellPhone}
            />

            <Input
              label="Data de nascimento:"
              type="date"
              placeholder="Digite sua data de nascimento"
              {...register('birthDate')}
              colorLabel={landingPageProcess.colorBase}
              error={errors.birthDate}
            />
          </Flex>

          {landingPageProcess.isAddress === "Y" && (<>
            <Flex flexDirection={['column', 'column', 'row']} gap="4">
              <Input
                as={InputMask}
                mask="99999-999"
                label="CEP:"
                type="text"
                placeholder="Digite seu cep"
                onBlurCapture={(e) => {handleSearchZipCode(e.target.value)}}
                {...register('zipCode')}
                colorLabel={landingPageProcess.colorBase}
                error={errors.zipCode}
              />

              <Input
                label="Endereço:"
                type="text"
                placeholder="Digite seu endereço"
                {...register('address')}
                colorLabel={landingPageProcess.colorBase}
                error={errors.address}
              />
            </Flex>

            <Flex flexDirection={['column', 'column', 'row']} gap="4">
              <Input
                label="Número:"
                type="text"
                placeholder="Digite o número"
                {...register('number')}
                colorLabel={landingPageProcess.colorBase}
                error={errors.number}
              />

              <Input
                label="Complemento:"
                type="text"
                placeholder="Digite o complemento"
                {...register('complement')}
                colorLabel={landingPageProcess.colorBase}
                error={errors.complement}
              />

              <Input
                label="Bairro:"
                type="text"
                placeholder="Digite seu bairro"
                {...register('district')}
                colorLabel={landingPageProcess.colorBase}
                error={errors.district}
              />
            </Flex>

            <Flex flexDirection={['column', 'column', 'row']} gap="4">
              <Input
                label="Cidade:"
                type="text"
                placeholder="Digite sua cidade"
                {...register('city')}
                colorLabel={landingPageProcess.colorBase}
                error={errors.city}
              />

              <Input
                label="UF:"
                type="text"
                placeholder="Digite a uf"
                maxLength={2}
                {...register('state')}
                colorLabel={landingPageProcess.colorBase}
                error={errors.state}
              />
            </Flex>
          </>)}

          {landingPageProcess.question1 && (
            <Input
              label={landingPageProcess.question1}
              type="text"
              {...register('question1')}
              colorLabel={landingPageProcess.colorBase}
              error={errors.question1}
            />
          )}

          {landingPageProcess.question2 && (
            <Input
              label={landingPageProcess.question2}
              type="text"
              {...register('question2')}
              colorLabel={landingPageProcess.colorBase}
              error={errors.question2}
            />
          )}

          {landingPageProcess.question3 && (
            <Input
              label={landingPageProcess.question3}
              type="text"
              {...register('question3')}
              colorLabel={landingPageProcess.colorBase}
              error={errors.question3}
            />
          )}

          {landingPageProcess.isNote === "Y" && (
            <TextArea
              label="Observação:"
              {...register('observation')}
              colorLabel={landingPageProcess.colorBase}
              error={errors.observation} 
            />
          )}

          <Text w="100%" color="red" textAlign="left">
            (*) campos obrigatório
          </Text>

          <Box>
            <Flex align="center" mt={1} mb={0}>
              <Checkbox
                isChecked={allChecked}
                isIndeterminate={isIndeterminate}
                label="Eu concordo em receber comunicações:"
                colorLabel={landingPageProcess.colorBase}
                name="allAccept"
                onChange={(e) =>
                  setCheckedItems([
                    e.target.checked,
                    e.target.checked,
                    e.target.checked,
                  ])
                }
              />

              <Button
                color={landingPageProcess.colorBase}
                _hover={{
                  bg: 'transparent',
                  color: landingPageProcess.colorHeader,
                }}
                onClick={() => setToastOptions(!toastOptions)}
                size="md"
              >
                ver opções
              </Button>
            </Flex>

            <Stack pl={6} spacing={2} display={toastOptions ? 'flex' : 'none'}>
              <Checkbox
                isChecked={checkedItems[0]}
                label="E-mail marketing."
                colorLabel={landingPageProcess.colorBase}
                name="acceptEmail"
                onChange={(e) =>
                  setCheckedItems([
                    e.target.checked,
                    checkedItems[1],
                    checkedItems[2],
                  ])
                }
              />
              <Checkbox
                isChecked={checkedItems[1]}
                label="SMS."
                colorLabel={landingPageProcess.colorBase}
                name="acceptSms"
                onChange={(e) =>
                  setCheckedItems([
                    checkedItems[0],
                    e.target.checked,
                    checkedItems[2],
                  ])
                }
              />
              <Checkbox
                isChecked={checkedItems[2]}
                label="WhatsApp."
                colorLabel={landingPageProcess.colorBase}
                name="acceptWhatsapp"
                onChange={(e) =>
                  setCheckedItems([
                    checkedItems[0],
                    checkedItems[1],
                    e.target.checked,
                  ])
                }
              />
            </Stack>
          </Box>

          {landingPageProcess.urlPrivacy && (
            <Text>
              Ao informar meus dados, eu concordo com a {` `}
              <Link
                href={landingPageProcess.urlPrivacy}
                color={landingPageProcess.colorBase}
                fontWeight="bold"
                target="_blank"
              >
                Política de Privacidade.
              </Link>
            </Text>
          )}
        </Flex>

        <Flex
          as={Button}
          type="submit"
          isLoading={isSubmitting}
          colorScheme="teal"
          variant="outline"
          spinnerPlacement="start"
          w="100%"
          maxW={250}
          h={50}
          bgColor={landingPageProcess.colorBase}
          color="#FFF"
          border="2px solid transparent"
          _hover={{
            borderColor: landingPageProcess.colorBase,
            bgColor: 'transparent',
            color: landingPageProcess.colorBase,
          }}
          fontWeight="bold"
          fontSize="20"
          borderRadius="12"
          justify="center"
          align="center"
          gap="2"
          my="8"
        >
          Cadastrar
        </Flex>
      </Flex>
    </Flex>
  )
}
